import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

import config from '@config'

interface StyledTeamLogoProps {
  hasError: boolean
  size: number
}

interface StyledImageProps {
  hasError: boolean
}

export interface TeamLogoProps {
  url: string
  name: string
  size: number
}

const {
  backend: { imageUrl },
} = config

const StyledTeamLogo = styled.div<StyledTeamLogoProps>`
  ${({
    theme: {
      colors: { iconDefaultDisabled },
    },
    hasError,
    size,
  }) => css`
    ${hasError &&
    css`
      background-color: ${iconDefaultDisabled};
      border-radius: 50%;
    `}
    height: ${size}px;
    width: ${size}px;
    min-width: ${size}px;
  `}
`

const StyledImage = styled.img<StyledImageProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      display: none;
    `};
  height: 100%;
  width: 100%;
  object-fit: contain;
`

const TeamLogo: FunctionComponent<TeamLogoProps> = ({ url, name, size }) => {
  const [error, setError] = useState<boolean>(false)
  const imageRef = useRef<HTMLImageElement>(null)

  const onError = useCallback(() => {
    setError(true)
  }, [])

  useEffect(() => {
    const currentImage = imageRef.current
    currentImage?.addEventListener('error', onError)
    return () => {
      currentImage?.removeEventListener('error', onError)
    }
  }, [onError])

  const imageSource = `${imageUrl}${url}?ratio=free&imwidth=${size}`

  return (
    <StyledTeamLogo hasError={error} size={size}>
      {url && (
        <StyledImage
          ref={imageRef}
          hasError={error}
          srcSet={`${imageSource}&imdensity=1 1x, ${imageSource}&imdensity=2 2x, ${imageSource}&imdensity=3 3x`}
          alt={name}
          loading="lazy"
          title={name}
        />
      )}
    </StyledTeamLogo>
  )
}

export default TeamLogo
